// AIRCAT 35 CREWLINER — Complete Component & Equipment Tree
// Extracted from Operations Manual — Energy Craft 01/02/03
// ABS Class — High-Speed Craft (HSC) Category A, Sea Area 2

export interface ComponentDef {
  name: string;
  make?: string;
  model?: string;
  specs?: string;
  criticality: "A" | "B" | "C"; // A=Critical, B=Essential, C=Standard
  location?: string;
  notes?: string;
  classSurveyItem?: boolean;
  maintenanceSource?: string;
}

export interface SubSystemDef {
  name: string;
  components: ComponentDef[];
}

export interface SystemDef {
  id: string;
  name: string;
  icon: string;
  category: string; // maps to pms_equipment.category
  subSystems: SubSystemDef[];
}

export interface MaintenanceJobTemplate {
  title: string;
  system: string;
  intervalDays?: number;
  intervalHours?: number;
  jobType: "calendar" | "running_hours" | "condition_based";
  department: string;
  priority: string;
  classRequirement?: boolean;
  description?: string;
  componentPattern?: string; // regex or keyword to link
}

// ─── 19 SYSTEMS ────────────────────────────────────────────────────────────
export const AIRCAT35_SYSTEMS: SystemDef[] = [
  // SYSTEM 1 — PROPULSION
  {
    id: "SYS-01", name: "Propulsion System", icon: "🔄", category: "propulsion",
    subSystems: [
      { name: "Main Engines", components: [
        { name: "Main Engine No.1", make: "MTU", model: "16V 2000 M72", specs: "1440 kW, 2250 rpm, IMO II / EPA 2 / EU IIIA", criticality: "A", location: "Engine Room", maintenanceSource: "OEM Manual", classSurveyItem: true },
        { name: "Main Engine No.2", make: "MTU", model: "16V 2000 M72", specs: "1440 kW, 2250 rpm", criticality: "A", location: "Engine Room", maintenanceSource: "OEM Manual", classSurveyItem: true },
        { name: "Main Engine No.3", make: "MTU", model: "16V 2000 M72", specs: "1440 kW, 2250 rpm", criticality: "A", location: "Engine Room", maintenanceSource: "OEM Manual", classSurveyItem: true },
        { name: "Main Engine No.4", make: "MTU", model: "16V 2000 M72", specs: "1440 kW, 2250 rpm", criticality: "A", location: "Engine Room", maintenanceSource: "OEM Manual", classSurveyItem: true },
      ]},
      { name: "Gearboxes", components: [
        { name: "Gearbox No.1", make: "TBC", model: "TBC", specs: "PTO-driven; hydraulic pump drive; waterjet coupling", criticality: "A", location: "Engine Room" },
        { name: "Gearbox No.2", make: "TBC", model: "TBC", specs: "PTO-driven", criticality: "A", location: "Engine Room" },
        { name: "Gearbox No.3", make: "TBC", model: "TBC", specs: "PTO-driven", criticality: "A", location: "Engine Room" },
        { name: "Gearbox No.4", make: "TBC", model: "TBC", specs: "PTO-driven", criticality: "A", location: "Engine Room" },
      ]},
      { name: "Propulsion Shafts", components: [
        { name: "Cardan Shaft No.1", make: "TBC", model: "TBC", specs: "Outboard/aft engine short shaft", criticality: "A", location: "Engine Room" },
        { name: "Cardan Shaft No.2", make: "TBC", model: "TBC", specs: "Outboard/aft engine short shaft", criticality: "A", location: "Engine Room" },
        { name: "Cardan Shaft No.3", make: "TBC", model: "TBC", specs: "Inboard/fwd engine long shaft", criticality: "A", location: "Engine Room" },
        { name: "Cardan Shaft No.4", make: "TBC", model: "TBC", specs: "Inboard/fwd engine long shaft", criticality: "A", location: "Engine Room" },
        { name: "Drive Shaft No.1", make: "TBC", model: "TBC", specs: "Universal joint alignment shaft to waterjet", criticality: "A", location: "Engine Room" },
        { name: "Drive Shaft No.2", make: "TBC", model: "TBC", specs: "Universal joint alignment shaft", criticality: "A", location: "Engine Room" },
        { name: "Drive Shaft No.3", make: "TBC", model: "TBC", specs: "Universal joint alignment shaft", criticality: "A", location: "Engine Room" },
        { name: "Drive Shaft No.4", make: "TBC", model: "TBC", specs: "Universal joint alignment shaft", criticality: "A", location: "Engine Room" },
      ]},
      { name: "Shaft Bearings", components: [
        { name: "Shaft Bearing No.1", make: "TBC", model: "TBC", criticality: "A", location: "Engine Room" },
        { name: "Shaft Bearing No.2", make: "TBC", model: "TBC", criticality: "A", location: "Engine Room" },
        { name: "Shaft Bearing No.3", make: "TBC", model: "TBC", criticality: "A", location: "Engine Room" },
        { name: "Shaft Bearing No.4", make: "TBC", model: "TBC", criticality: "A", location: "Engine Room" },
      ]},
      { name: "Waterjets", components: [
        { name: "Waterjet No.1", make: "Rolls Royce Kamewa", model: "S50-3/CA", specs: "2060 kW, high-speed steel, reversing buckets", criticality: "A", maintenanceSource: "OEM Manual", classSurveyItem: true },
        { name: "Waterjet No.2", make: "Rolls Royce Kamewa", model: "S50-3/CA", specs: "2060 kW", criticality: "A", maintenanceSource: "OEM Manual", classSurveyItem: true },
        { name: "Waterjet No.3", make: "Rolls Royce Kamewa", model: "S50-3/CA", specs: "2060 kW", criticality: "A", maintenanceSource: "OEM Manual", classSurveyItem: true },
        { name: "Waterjet No.4", make: "Rolls Royce Kamewa", model: "S50-3/CA", specs: "2060 kW", criticality: "A", maintenanceSource: "OEM Manual", classSurveyItem: true },
        { name: "Waterjet Hydraulic Valve Block", make: "TBC", model: "TBC", specs: "Mounted on WJ No.2; controls all 4 jets steering/reversing", criticality: "A" },
      ]},
    ],
  },

  // SYSTEM 2 — ELECTRIC POWER GENERATION
  {
    id: "SYS-02", name: "Electric Power Generation", icon: "⚡", category: "electrical",
    subSystems: [
      { name: "Diesel Generator Sets", components: [
        { name: "Generator Set No.1", make: "Cummins Onan", model: "MDDCT 65 kW – 4045 TFM85", specs: "65 kW, 3Ph 60 Hz, 1800 rpm, 24 VDC battery", criticality: "A", maintenanceSource: "OEM Manual", classSurveyItem: true },
        { name: "Generator Set No.2", make: "Cummins Onan", model: "MDDCT 65 kW – 4045 TFM85", specs: "65 kW, 3Ph 60 Hz, 1800 rpm", criticality: "A", maintenanceSource: "OEM Manual", classSurveyItem: true },
      ]},
      { name: "Electrical Switchboards", components: [
        { name: "415V / 230V AC Main Switchboard (MSB)", make: "TBC", model: "TBC", specs: "Two single-bus system with bus tie", criticality: "A", location: "Crew Mess" },
        { name: "24V DC Main Switchboard", make: "TBC", model: "TBC", criticality: "A", location: "Crew Mess" },
        { name: "24V DC Emergency Switchboard", make: "TBC", model: "TBC", specs: "Emergency battery bank supply", criticality: "A" },
      ]},
      { name: "Battery Banks", components: [
        { name: "ME Starter Battery Bank No.1", specs: "24V DC", criticality: "A" },
        { name: "ME Starter Battery Bank No.2", specs: "24V DC", criticality: "A" },
        { name: "ME Starter Battery Bank No.3", specs: "24V DC", criticality: "A" },
        { name: "ME Starter Battery Bank No.4", specs: "24V DC", criticality: "A" },
        { name: "Genset Starter Battery Bank No.1", specs: "24V DC", criticality: "A" },
        { name: "Genset Starter Battery Bank No.2", specs: "24V DC", criticality: "A" },
        { name: "Lift Fan Engine Starter Battery No.1", specs: "24V DC", criticality: "A" },
        { name: "Lift Fan Engine Starter Battery No.2", specs: "24V DC", criticality: "A" },
        { name: "Service Battery Bank", specs: "24V DC; main deck external box", criticality: "B" },
        { name: "Emergency Battery Bank", specs: "24V DC; main deck external box", criticality: "A" },
        { name: "GMDSS Dedicated Battery", specs: "24V DC; charged via emergency switchboard", criticality: "A" },
      ]},
    ],
  },

  // SYSTEM 3 — SURFACE EFFECT SYSTEM (SES)
  {
    id: "SYS-03", name: "Surface Effect System (SES)", icon: "🌊", category: "propulsion",
    subSystems: [
      { name: "SES Control System", components: [
        { name: "SES Control Cabinet / IPC", make: "Beckhoff IPC (PLC)", model: "C6015", specs: "Ethernet interfaces, HMI touchscreen, VPN remote access", criticality: "A" },
        { name: "SES HMI Touchscreen", make: "TBC", model: "TBC", specs: "Bridge-mounted HMI panel", criticality: "B" },
      ]},
      { name: "Lift Fans", components: [
        { name: "Lift Fan Engine No.1", make: "TBC", model: "TBC", specs: "With dedicated alternator", criticality: "A" },
        { name: "Lift Fan Engine No.2", make: "TBC", model: "TBC", specs: "With dedicated alternator", criticality: "A" },
        { name: "Lift Fan Unit No.1", make: "TBC", model: "TBC", specs: "Forward lift fan; two bearings (fwd/aft)", criticality: "A" },
        { name: "Lift Fan Unit No.2", make: "TBC", model: "TBC", specs: "Forward lift fan; two bearings", criticality: "A" },
        { name: "Lift Fan Bearing – Fan 1 Forward", make: "TBC", model: "TBC", specs: "Grease-lubricated", criticality: "A" },
        { name: "Lift Fan Bearing – Fan 1 Aft", make: "TBC", model: "TBC", specs: "Grease-lubricated", criticality: "A" },
        { name: "Lift Fan Bearing – Fan 2 Forward", make: "TBC", model: "TBC", specs: "Grease-lubricated", criticality: "A" },
        { name: "Lift Fan Bearing – Fan 2 Aft", make: "TBC", model: "TBC", specs: "Grease-lubricated", criticality: "A" },
      ]},
      { name: "SES Dampers", components: [
        { name: "RCS Damper No.1", make: "TBC", model: "TBC", specs: "With drainage holes; actuator and damper blades", criticality: "A" },
        { name: "RCS Damper No.2", make: "TBC", model: "TBC", specs: "With drainage holes", criticality: "A" },
        { name: "Lift Fan Damper No.1", make: "TBC", model: "TBC", specs: "Deactivated when lift fan disabled", criticality: "A" },
        { name: "Lift Fan Damper No.2", make: "TBC", model: "TBC", specs: "Deactivated when lift fan disabled", criticality: "A" },
      ]},
      { name: "SES Pressure Sensors", components: [
        { name: "Air Cushion Pressure Sensor No.1", criticality: "A", location: "Air cushion" },
        { name: "Air Cushion Pressure Sensor No.2", criticality: "A", location: "Air cushion" },
        { name: "Stern Bag Pressure Sensor No.1", criticality: "A", location: "Stern bag" },
        { name: "Stern Bag Pressure Sensor No.2", criticality: "A", location: "Stern bag" },
      ]},
      { name: "Rubber Seals (Hull Equipment)", components: [
        { name: "Bow Skirt Finger No.1", specs: "156 kg dry; neoprene-rubber/nylon fiber", criticality: "A" },
        { name: "Bow Skirt Finger No.2", specs: "Interchangeable with Fingers 1–7", criticality: "A" },
        { name: "Bow Skirt Finger No.3", criticality: "A" },
        { name: "Bow Skirt Finger No.4", criticality: "A" },
        { name: "Bow Skirt Finger No.5", criticality: "A" },
        { name: "Bow Skirt Finger No.6", criticality: "A" },
        { name: "Bow Skirt Finger No.7", criticality: "A" },
        { name: "Stern Rubber Bag (3-Loop Assembly)", specs: "611 kg dry; natural/synthetic rubber-coated nylon; 3 inflated loops", criticality: "A" },
      ]},
    ],
  },

  // SYSTEM 4 — FUEL OIL SYSTEM
  {
    id: "SYS-04", name: "Fuel Oil System", icon: "⛽", category: "fuel",
    subSystems: [
      { name: "Fuel Tanks", components: [
        { name: "Main Fuel Oil Storage Tank(s)", specs: "Capacity TBC (Ops Manual §15.2)", criticality: "A" },
        { name: "Daily Service Tank(s)", criticality: "A" },
      ]},
      { name: "Fuel Purification", components: [
        { name: "Fuel Purifier / Separator No.1", make: "Rellumix", model: "DT 223-037", specs: "3 m³/h transfer/purifying capacity", criticality: "B" },
        { name: "Fuel Purifier / Separator No.2", make: "Rellumix", model: "DT 223-037", specs: "3 m³/h", criticality: "B" },
      ]},
    ],
  },

  // SYSTEM 5 — FIRE MAIN SYSTEM
  {
    id: "SYS-05", name: "Fire Main System", icon: "🔥", category: "safety",
    subSystems: [
      { name: "Fire Pumps", components: [
        { name: "PTO Fire Pump (Deluge/Fire)", make: "Jason", model: "JMCU-2000HA-K 01.30", specs: "300 m³/h; PTO-driven; self-priming; also deluge & emergency bilge pump", criticality: "A", location: "Fwd Stbd Engine Room", classSurveyItem: true },
        { name: "Electric Fire Pump", make: "TBC", model: "TBC", specs: "Self-priming with separate priming pump; emergency bilge backup", criticality: "A", location: "Fwd Port Engine Room", classSurveyItem: true },
      ]},
      { name: "Fire Main Distribution", components: [
        { name: "Fire Hydrant (Port Deck)", criticality: "A" },
        { name: "Fire Hydrant (Stbd Deck)", criticality: "A" },
        { name: "International Shore Connection", criticality: "B" },
        { name: "Sea Chest (Fire Main)", criticality: "A" },
        { name: "Fire Main Strainer", criticality: "A" },
      ]},
    ],
  },

  // SYSTEM 6 — FIRE FIGHTING & DETECTION
  {
    id: "SYS-06", name: "Fire Fighting & Detection", icon: "🧯", category: "safety",
    subSystems: [
      { name: "Fire Detection", components: [
        { name: "EPAS Fire Detection System (Engine Room)", make: "TBC", model: "TBC", specs: "Auto-triggers ER ventilation shutdown and aerosol FF", criticality: "A", classSurveyItem: true },
        { name: "Fire Alarm Control Panel", make: "TBC", model: "TBC", criticality: "A", location: "Bridge / Wheelhouse" },
        { name: "Smoke/Heat Detectors", specs: "Multiple; throughout vessel", criticality: "A" },
      ]},
      { name: "Portable Fire Extinguishers", components: [
        { name: "Portable CO₂ / Dry Powder Extinguishers", specs: "Per Safety & Fire Plan", criticality: "A", classSurveyItem: true },
        { name: "Fire Blankets", specs: "Per Safety & Fire Plan", criticality: "B" },
      ]},
      { name: "Fixed Fire Suppression", components: [
        { name: "Aerosol Fixed FF System", make: "TBC", model: "TBC", specs: "1 per ER", criticality: "A", location: "Engine Rooms", classSurveyItem: true },
        { name: "Deluge FF System", make: "TBC", model: "TBC", specs: "Fed by PTO fire pump", criticality: "A", location: "Forward deck" },
      ]},
    ],
  },

  // SYSTEM 7 — BILGE SYSTEM
  {
    id: "SYS-07", name: "Bilge System", icon: "💧", category: "auxiliary",
    subSystems: [
      { name: "Primary Bilge System", components: [
        { name: "Submersible Bilge Pump (×20)", make: "KPM", model: "KPM350S", specs: "167 L/min @ 3m head; float switch; 20 units (1 per compartment)", criticality: "B" },
        { name: "Portable Emergency Bilge Pump", make: "KPM", model: "KPM350 24V Portable", specs: "167 L/min @ 3m head; 10m cable; stowed aft port deck", criticality: "A" },
      ]},
      { name: "Secondary Bilge System (ER)", components: [
        { name: "Secondary ER Bilge (via Fire Pumps)", specs: "Fire pumps cross-connected to opposing-side bilge overboard valve", criticality: "A" },
        { name: "Lift Fan Compartment Bilge Pump – Fwd", make: "KPM", specs: "Active only with 415V AC", criticality: "B" },
        { name: "Lift Fan Compartment Bilge Pump – Aft", make: "KPM", specs: "Active only with 415V AC", criticality: "B" },
      ]},
    ],
  },

  // SYSTEM 8 — SEWAGE SYSTEM
  {
    id: "SYS-08", name: "Sewage System", icon: "🚿", category: "auxiliary",
    subSystems: [
      { name: "Black Water System", components: [
        { name: "Black Water Holding Tank", make: "TBC", specs: "1 m³; camlock connector", criticality: "B", location: "Port Jet Space" },
        { name: "Black Water Macerator Pump", make: "Rheinstrom", model: "FS 370", specs: "50 L/min @ 6m head", criticality: "B" },
      ]},
      { name: "Grey Water System", components: [
        { name: "Grey Water Holding Tank", make: "TBC", specs: "1 m³; camlock connector", criticality: "B", location: "Stbd Jet Space" },
        { name: "Grey Water Macerator Pump", make: "Rheinstrom", model: "FS 370", specs: "50 L/min @ 6m head", criticality: "B" },
      ]},
    ],
  },

  // SYSTEM 9 — FRESH WATER SYSTEM
  {
    id: "SYS-09", name: "Fresh Water System", icon: "🚰", category: "auxiliary",
    subSystems: [
      { name: "FW Storage & Distribution", components: [
        { name: "Fresh Water Tank", specs: "With low/high level alarms to AMS", criticality: "B", location: "Stbd, Fr6–Fr7" },
        { name: "FW Domestic Pump No.1", make: "Rolid Pedrollo", model: "JSWm2C", specs: "3 m³/h @ 24m head; pressure set", criticality: "B" },
        { name: "FW Domestic Pump No.2", make: "Rolid Pedrollo", model: "JSWm2C", specs: "Standby", criticality: "B" },
        { name: "Pressure Set Tank", criticality: "B" },
        { name: "Hot Water Unit – Washrooms", make: "Rheem", model: "XC-15 W", specs: "15 L electric storage heater", criticality: "C" },
        { name: "Hot Water Unit – Mess (Instant)", make: "Bennington", model: "s630m", specs: "Instant heat electric shower", criticality: "C" },
      ]},
    ],
  },

  // SYSTEM 10 — RAW WATER COOLING
  {
    id: "SYS-10", name: "Raw Water Machinery Cooling", icon: "❄️", category: "engine",
    subSystems: [
      { name: "Sea Chests & Strainers", components: [
        { name: "Sea Chest (ME No.1)", specs: "Individual bottom shell opening", criticality: "A" },
        { name: "Sea Chest (ME No.2)", criticality: "A" },
        { name: "Sea Chest (ME No.3)", criticality: "A" },
        { name: "Sea Chest (ME No.4)", criticality: "A" },
        { name: "Sea Chest (Generator No.1)", criticality: "A" },
        { name: "Sea Chest (Generator No.2)", criticality: "A" },
        { name: "Sea Chest (Lift Fan Engine No.1)", criticality: "A" },
        { name: "Sea Chest (Lift Fan Engine No.2)", criticality: "A" },
        { name: "Sea Chest (Fire Main)", criticality: "A" },
        { name: "Sea Chest (Deluge Pump)", criticality: "A" },
      ]},
      { name: "Generator Raw Water Pumps", components: [
        { name: "Generator Raw Water Pump No.1", make: "DESMI", model: "ESL 50-180N/A12", specs: "27.3 m³/h @ 9.8m head; 2.53 kW / 1750 RPM", criticality: "A" },
        { name: "Generator Raw Water Pump No.2", make: "DESMI", model: "ESL 50-180N/A12", specs: "27.3 m³/h", criticality: "A" },
      ]},
    ],
  },

  // SYSTEM 11 — AUXILIARY SYSTEMS
  {
    id: "SYS-11", name: "Auxiliary Systems", icon: "🔧", category: "auxiliary",
    subSystems: [
      { name: "Hydraulic System (Waterjet Steering/Bucket)", components: [
        ...Array.from({ length: 4 }, (_, i) => ([
          { name: `Hydraulic System No.${i+1} (Jet ${i+1}) – Tank`, specs: "Independent; PTO-driven pump off Gearbox", criticality: "B" as const },
          { name: `Hydraulic System No.${i+1} – Manifold`, criticality: "B" as const },
          { name: `Hydraulic System No.${i+1} – Oil Cooler`, criticality: "B" as const },
        ])).flat(),
      ]},
      { name: "Anchor System", components: [
        { name: "Anchor Windlass", make: "Hypac", model: "HHAW15-32 A0000", specs: "172 kN; 3Ph, 12A peak, 380–440V, 50Hz", criticality: "B", location: "Port side deck, Fr#22" },
        { name: "Anchor Winch Hydraulic Powerpack", specs: "Dedicated hydraulic unit for windlass only", criticality: "B", location: "Below deck, Fr#21–22" },
        { name: "Anchor & Chain", specs: "Bottom layer always retained on drum", criticality: "B" },
      ]},
      { name: "Air Conditioning System", components: [
        { name: "AC Outdoor Unit (Condensing Unit) ×4", specs: "Air-cooled; hi-low pressure switch protected", criticality: "C" },
        { name: "AC Indoor Fan Coil Unit (FCU) – Ceiling Cassette ×6", specs: "Pax cabin, crew mess, wheelhouse", criticality: "C" },
      ]},
      { name: "ER Ventilation", components: [
        { name: "ER Supply Fan No.1 (Port)", specs: "Fire damper below deck; louvers outboard", criticality: "B" },
        { name: "ER Supply Fan No.2 (Stbd)", criticality: "B" },
        { name: "ER Exhaust Fan No.1 (Port)", criticality: "B" },
        { name: "ER Exhaust Fan No.2 (Stbd)", criticality: "B" },
      ]},
      { name: "Below Hull Ventilation", components: [
        { name: "FO & Pump Room Supply Fan", specs: "Fwd supply, aft exhaust", criticality: "B" },
        { name: "FO & Pump Room Exhaust Fan", criticality: "B" },
        { name: "Passive Vent Pipes (Waterjet/Void Spaces)", specs: "Through-hull; passive only", criticality: "C" },
      ]},
    ],
  },

  // SYSTEM 12 — NAVIGATIONAL EQUIPMENT
  {
    id: "SYS-12", name: "Navigational Equipment", icon: "🧭", category: "navigation",
    subSystems: [
      { name: "Radar Systems", components: [
        { name: "S-Band ARPA Radar", make: "Furuno", model: "FAR-2218BB/PM", specs: "3 GHz; 8ft antenna; 24\" display; ~7NM surface", criticality: "A", classSurveyItem: true },
        { name: "X-Band ARPA Radar", make: "Furuno", model: "FAR-2238SBB/PM/A", specs: "9.4 GHz; 4ft antenna; 24\" display", criticality: "A", classSurveyItem: true },
      ]},
      { name: "Position & Heading", components: [
        { name: "GPS Navigator", make: "Furuno", model: "GP-170 + GPA-017S", specs: "SOLAS compliant", criticality: "A", classSurveyItem: true },
        { name: "Gyro Compass", make: "Sperry Marine", model: "NAVIGAT 2500", specs: "Fiber optic; maintenance-free", criticality: "A", classSurveyItem: true },
        { name: "Satellite Compass", make: "Furuno", model: "SC-70/MIC/A", specs: "GPS/Galileo/GLONASS; backup gyro", criticality: "B" },
        { name: "Magnetic Compass", make: "Lilley & Gillie", model: "SR4 Class A", specs: "MED type-approved; independent of power", criticality: "A", classSurveyItem: true },
      ]},
      { name: "Navigation Sensors", components: [
        { name: "Autopilot", make: "Sperry Marine", model: "NAVIPILOT 4500N", specs: "Adaptive heading control; waterjet steering sync", criticality: "B" },
        { name: "AIS Transceiver", make: "Furuno", model: "FA-1702", specs: "SOLAS Ch.V Reg.19; VHF range ~40 NM", criticality: "A", classSurveyItem: true },
        { name: "NAVTEX Receiver", make: "Furuno", model: "NX-700B", specs: "518 kHz international + 490/4209.5 kHz", criticality: "B" },
        { name: "Speed Log", make: "Furuno", model: "DS-85 Doppler", specs: "Fr#18–19 bottom hull transducer", criticality: "B" },
        { name: "Echo Sounder", make: "Furuno", model: "FE-800", specs: "30m effective depth; 12h data storage", criticality: "B", classSurveyItem: true },
        { name: "Anemometer / Wind Sensor", make: "LCJ Capteurs", model: "CV3F", specs: "12V; wind speed, direction, temperature", criticality: "C" },
        { name: "ECDIS No.1", make: "Furuno", model: "FMD-3100-E", specs: "IACS UR E27 compliant", criticality: "A", classSurveyItem: true },
        { name: "ECDIS No.2", make: "Furuno", model: "FMD-3100-E", specs: "Duplicate; fully paperless navigation", criticality: "A", classSurveyItem: true },
      ]},
      { name: "Bridge Safety & Communication", components: [
        { name: "BNWAS", specs: "Dormant period 3–12 min; 3-stage alarm; emergency power backup", criticality: "A", classSurveyItem: true },
        { name: "SEA.AI Sentry System", make: "SEA.AI", model: "Sentry", specs: "2x thermal/IR + 2x optical cameras; AI object detection; 360° mast-mounted", criticality: "B" },
        { name: "Vessel Horn", model: "EW3 Electric Whistle", specs: "130 dB output; mast-mounted", criticality: "A" },
        { name: "Navigation Lights", make: "Beng Hui Marine", specs: "Full COLREGS set: masthead, port, stbd, stern, NUC, anchor", criticality: "A", classSurveyItem: true },
        { name: "Search Light – Port", make: "Francis", model: "L300RC LED 234W", specs: "365° horiz / +35/-40° elevation; joystick", criticality: "B" },
        { name: "Search Light – Stbd", make: "Francis", model: "L300RC LED 234W", specs: "As above; stbd console joystick", criticality: "B" },
        { name: "Aldis Lamp", specs: "SOLAS portable day/night signaling lamp", criticality: "B" },
      ]},
    ],
  },

  // SYSTEM 13 — GMDSS / RADIO COMMUNICATIONS
  {
    id: "SYS-13", name: "GMDSS / Radio Communications", icon: "📡", category: "navigation",
    subSystems: [
      { name: "Radio Communications", components: [
        { name: "VHF DSC Radio No.1", make: "Furuno", model: "FM-8900S", specs: "25W; Class-A DSC; Ch70 watch receiver; A1 ~40 NM", criticality: "A", classSurveyItem: true },
        { name: "VHF DSC Radio No.2", make: "Furuno", model: "FM-8900S", specs: "A1: ~40 NM", criticality: "A", classSurveyItem: true },
        { name: "MF/HF DSC Radio", make: "Furuno", model: "FS-2575", specs: "250W; A2/A3 worldwide", criticality: "A", classSurveyItem: true },
        { name: "Inmarsat-C Terminal No.1", make: "Furuno", model: "Felcom-18", specs: "Text, distress alerting, EGC/SafetyNET; A3", criticality: "A", classSurveyItem: true },
        { name: "Inmarsat-C Terminal No.2", make: "Furuno", model: "Felcom-18", specs: "Redundant unit", criticality: "A", classSurveyItem: true },
        { name: "SSAS / LRIT Terminal", make: "Furuno", model: "Felcom-19", specs: "Per Ship Security Manual; global", criticality: "A", classSurveyItem: true },
        { name: "Portable VHF GMDSS Radio (×3)", make: "Jotron", model: "Tron TR30", specs: "Waterproof; floatable; Ch16; GMDSS lithium batteries", criticality: "A", classSurveyItem: true },
        { name: "VSAT System", make: "Sailor", model: "1000 XTR KU", specs: "Ku-band; 1m dome; iDirect x7 router; NOT GMDSS", criticality: "C" },
        { name: "Iridium L-Band", make: "Sailor", model: "4300 L-Band", specs: "Voice/data backup; NOT GMDSS; global", criticality: "B" },
        { name: "Starlink System (×2)", make: "SpaceX Starlink", specs: "100–300+ Mbps; crew welfare/ops data; NOT GMDSS", criticality: "C" },
      ]},
    ],
  },

  // SYSTEM 14 — LIFE SAVING APPLIANCES
  {
    id: "SYS-14", name: "Life Saving Appliances (LSA)", icon: "🛟", category: "safety",
    subSystems: [
      { name: "LSA Equipment", components: [
        { name: "EPIRB", specs: "Float-free hydrostatic release; annual inspection/battery check", criticality: "A", classSurveyItem: true },
        { name: "SART", specs: "Annual inspection", criticality: "A", classSurveyItem: true },
        { name: "Life Jackets", specs: "Per complement; per SOLAS", criticality: "A", classSurveyItem: true },
        { name: "Life Rafts", specs: "SOLAS Pack B; annual service by approved station", criticality: "A", classSurveyItem: true },
        { name: "Life Buoys", specs: "Per Safety & Fire Plan", criticality: "A", classSurveyItem: true },
        { name: "Line Throwing Appliance", specs: "Annual inspection/replace projectile", criticality: "B" },
        { name: "Pyrotechnic Signaling Devices", specs: "Replace before expiry", criticality: "B" },
        { name: "MOB Recovery System / Rescue Sling", specs: "MOB recovery davit; rescue zone Stbd midship", criticality: "A" },
        { name: "Immersion Suits / Thermal Protective Aids", specs: "Per complement; annual inspection", criticality: "A", classSurveyItem: true },
        { name: "First Aid Kit / Medical Equipment", specs: "Inspect per schedule; replace expired items", criticality: "B" },
      ]},
    ],
  },

  // SYSTEM 15 — VOYAGE DATA RECORDER
  {
    id: "SYS-15", name: "Voyage Data Recorder (VDR)", icon: "📦", category: "navigation",
    subSystems: [
      { name: "VDR Components", components: [
        { name: "VDR Data Collecting Unit", make: "Furuno", model: "VR-7010-1E", specs: "SSD up to 720h (30 days) storage", criticality: "A", location: "Wheelhouse", classSurveyItem: true },
        { name: "Float-Free DRU (×2)", make: "Furuno", model: "VR-7024F", specs: "≥48h IMO-compliant; hydrostatic release ~4m", criticality: "A", location: "Wheelhouse roof", classSurveyItem: true },
        { name: "Fixed DRU (Protective Capsule)", make: "Furuno", model: "VR-7020", criticality: "A", location: "Port wheelhouse roof" },
        { name: "VDR Remote Alarm Panel", make: "Furuno", model: "VR-7017", criticality: "B", location: "Stbd overhead wheelhouse" },
        { name: "VDR Bridge Microphone (×4)", make: "Furuno", model: "VR-7011", criticality: "B", location: "Wheelhouse ceiling" },
        { name: "VDR External Waterproof Microphone (×2)", make: "Furuno", model: "VR-7012W", criticality: "B", location: "Superstructure fwd entry doors" },
      ]},
    ],
  },

  // SYSTEM 16 — CCTV SYSTEM
  {
    id: "SYS-16", name: "CCTV System", icon: "📹", category: "electrical",
    subSystems: [
      { name: "CCTV Components", components: [
        { name: "Network Video Recorder (NVR)", make: "Dahua", model: "DHI-NVR4216-KS2/L", specs: "21-camera system; wheelhouse trackball control", criticality: "B" },
        { name: "External Dome Camera (×11)", make: "Dahua", model: "XNV-6080SA", specs: "2MP IR dome; deck, ER, fwd deck, stern, rescue zone", criticality: "B" },
        { name: "Internal Dome Camera (×10)", make: "WISNET", model: "DH-IPC-HDBW2230E-S-S2", specs: "2MP IR dome; pax cabin, wheelhouse", criticality: "C" },
        { name: "CCTV System Monitor", make: "Samsung", model: "SMT-1935P 19\" LED", criticality: "C", location: "Fwd overhead wheelhouse" },
        { name: "POE Switch", make: "Dahua", model: "DH-PFS4218-16ET-190", specs: "System PoE infrastructure", criticality: "C" },
      ]},
    ],
  },

  // SYSTEM 17 — AUTOMATIC MONITORING SYSTEM
  {
    id: "SYS-17", name: "Automatic Monitoring System (AMS)", icon: "📊", category: "electrical",
    subSystems: [
      { name: "AMS Components", components: [
        { name: "AMS Main Control Panel (Bridge)", specs: "Monitors: tank levels, bilge alarms, fire alarms, engine parameters", criticality: "A" },
        { name: "AMS Sensor Network", specs: "Tank gauging, bilge float switches, temp/pressure sensors throughout vessel", criticality: "A" },
      ]},
    ],
  },

  // SYSTEM 18 — VESSEL CONTROL SYSTEM
  {
    id: "SYS-18", name: "Vessel Control System (CanMan Touch)", icon: "🎮", category: "navigation",
    subSystems: [
      { name: "CanMan Touch Controls", components: [
        { name: "CanMan Touch Helm Control (Main)", specs: "Bridge; propulsion, SES, steering; touchscreen + joystick lever", criticality: "A" },
        { name: "CanMan Touch Helm Control (Secondary / Backup)", specs: "Back-up controls; for servicing/failure", criticality: "A" },
      ]},
    ],
  },

  // SYSTEM 19 — STRUCTURE & HULL
  {
    id: "SYS-19", name: "Structure & Hull", icon: "🚢", category: "hull",
    subSystems: [
      { name: "Hull & Structure", components: [
        { name: "Hull Structure (Welded Aluminum)", specs: "Main hull, side hulls, wet deck, foundations", criticality: "A", classSurveyItem: true },
        { name: "Resilient Mounts (Machinery)", specs: "Outboard mounts accessible; full survey requires inspection team", criticality: "B" },
        { name: "Cathodic Protection – Sacrificial Anodes", specs: "Bottom shell and appendages", criticality: "B", classSurveyItem: true },
        { name: "Anti-Corrosion Painting System", specs: "Marine paint system per spec", criticality: "B" },
        { name: "Mooring Equipment (Cleats, Fairleads, Lines)", specs: "Fwd, midship, aft", criticality: "B" },
      ]},
    ],
  },
];

// ─── MAINTENANCE JOB TEMPLATES ─────────────────────────────────────────────
export const AIRCAT35_MAINTENANCE_TEMPLATES: MaintenanceJobTemplate[] = [
  // DAILY
  { title: "Pre-start checks (oil, water, leaks, noise)", system: "Propulsion System", intervalDays: 1, jobType: "calendar", department: "engine", priority: "high", componentPattern: "Main Engine" },
  { title: "Shaft guards check; cardan shaft splines clean & greased", system: "Propulsion System", intervalDays: 1, jobType: "calendar", department: "engine", priority: "medium", componentPattern: "Shaft" },
  { title: "Bearing noise/vibration check and grease per OEM", system: "Propulsion System", intervalDays: 1, jobType: "calendar", department: "engine", priority: "medium", componentPattern: "Shaft Bearing" },
  { title: "Visual inspect fuel valves, pipework, connections", system: "Fuel Oil System", intervalDays: 1, jobType: "calendar", department: "engine", priority: "medium", componentPattern: "Fuel" },
  { title: "Visual check fire pumps and pipework", system: "Fire Main System", intervalDays: 1, jobType: "calendar", department: "deck", priority: "high", componentPattern: "Fire Pump" },
  { title: "Visual check AC for refrigerant leaks", system: "Auxiliary Systems", intervalDays: 1, jobType: "calendar", department: "engine", priority: "low", componentPattern: "AC" },
  { title: "Visual check ER ventilation fans and fire dampers", system: "Auxiliary Systems", intervalDays: 1, jobType: "calendar", department: "engine", priority: "medium", componentPattern: "ER" },
  { title: "Visual check below hull ventilation for damage/water", system: "Auxiliary Systems", intervalDays: 1, jobType: "calendar", department: "engine", priority: "low", componentPattern: "Pump Room" },
  { title: "Sewage piping and valves check for damage/leaks", system: "Sewage System", intervalDays: 1, jobType: "calendar", department: "engine", priority: "low", componentPattern: "Water" },
  { title: "Clean CanMan Touch touchscreen", system: "Vessel Control System (CanMan Touch)", intervalDays: 1, jobType: "calendar", department: "deck", priority: "low", componentPattern: "CanMan" },

  // WEEKLY
  { title: "Universal joints inspection; shaft coupling check; noise/vibration run-up", system: "Propulsion System", intervalDays: 7, jobType: "calendar", department: "engine", priority: "medium", componentPattern: "Shaft" },
  { title: "Operational fuel valve checks", system: "Fuel Oil System", intervalDays: 7, jobType: "calendar", department: "engine", priority: "medium", componentPattern: "Fuel" },
  { title: "Test all infrequently-used CanMan Touch functions", system: "Vessel Control System (CanMan Touch)", intervalDays: 7, jobType: "calendar", department: "deck", priority: "low", componentPattern: "CanMan" },

  // EVERY 14 DAYS
  { title: "SES pressure sensor calibration check via HMI", system: "Surface Effect System (SES)", intervalDays: 14, jobType: "calendar", department: "engine", priority: "high", componentPattern: "Pressure Sensor" },

  // MONTHLY
  { title: "Inspect and torque all shaft fixings", system: "Propulsion System", intervalDays: 30, jobType: "calendar", department: "engine", priority: "medium", componentPattern: "Shaft" },
  { title: "Hull visual inspection; log all findings", system: "Structure & Hull", intervalDays: 30, jobType: "calendar", department: "deck", priority: "high", classRequirement: true, componentPattern: "Hull Structure" },
  { title: "Actuate all non-overboard sewage valves; test for free movement", system: "Sewage System", intervalDays: 30, jobType: "calendar", department: "engine", priority: "low", componentPattern: "Water" },
  { title: "Clean FCU inlet filters; clean outdoor unit fins; check electrical connections", system: "Auxiliary Systems", intervalDays: 30, jobType: "calendar", department: "engine", priority: "low", componentPattern: "AC" },
  { title: "Test ER damper actuation; check air intake; lubricate moving parts", system: "Auxiliary Systems", intervalDays: 30, jobType: "calendar", department: "engine", priority: "medium", componentPattern: "ER" },
  { title: "Check below hull vent grills in place; lubricate closing flaps", system: "Auxiliary Systems", intervalDays: 30, jobType: "calendar", department: "engine", priority: "low", componentPattern: "Pump Room" },

  // EVERY 50 HOURS
  { title: "Bow skirt finger inspection (diver or pull-up method)", system: "Surface Effect System (SES)", intervalHours: 50, jobType: "running_hours", department: "deck", priority: "high", componentPattern: "Bow Skirt" },
  { title: "Stern bag inspection (internal/external surfaces)", system: "Surface Effect System (SES)", intervalHours: 50, jobType: "running_hours", department: "deck", priority: "high", componentPattern: "Stern Rubber" },

  // EVERY 6 MONTHS
  { title: "Lubricate all lift fan bearings with grease gun", system: "Surface Effect System (SES)", intervalDays: 180, jobType: "calendar", department: "engine", priority: "medium", componentPattern: "Lift Fan Bearing" },
  { title: "Check drainage holes on all RCS and Lift Fan dampers", system: "Surface Effect System (SES)", intervalDays: 180, jobType: "calendar", department: "engine", priority: "medium", componentPattern: "Damper" },

  // ANNUAL
  { title: "Shaft bearing inspection and replace where necessary; Chockfast condition check", system: "Propulsion System", intervalDays: 365, jobType: "calendar", department: "engine", priority: "high", classRequirement: true, componentPattern: "Shaft Bearing" },
  { title: "Full sewage system pump/discharge test; tank/pipe flush", system: "Sewage System", intervalDays: 365, jobType: "calendar", department: "engine", priority: "medium", componentPattern: "Water" },
  { title: "Echo sounder transducer inspection", system: "Navigational Equipment", intervalDays: 365, jobType: "calendar", department: "navigation", priority: "medium", classRequirement: true, componentPattern: "Echo Sounder" },
  { title: "EPIRB inspection / battery check", system: "Life Saving Appliances (LSA)", intervalDays: 365, jobType: "calendar", department: "safety", priority: "high", classRequirement: true, componentPattern: "EPIRB" },
  { title: "SART annual inspection", system: "Life Saving Appliances (LSA)", intervalDays: 365, jobType: "calendar", department: "safety", priority: "high", classRequirement: true, componentPattern: "SART" },
  { title: "Life jacket inspection", system: "Life Saving Appliances (LSA)", intervalDays: 365, jobType: "calendar", department: "safety", priority: "high", classRequirement: true, componentPattern: "Life Jacket" },
  { title: "Life raft service (approved station)", system: "Life Saving Appliances (LSA)", intervalDays: 365, jobType: "calendar", department: "safety", priority: "high", classRequirement: true, componentPattern: "Life Raft" },
  { title: "GMDSS annual radio survey (RMI-approved surveyor)", system: "GMDSS / Radio Communications", intervalDays: 365, jobType: "calendar", department: "navigation", priority: "high", classRequirement: true, componentPattern: "VHF|MF/HF|Inmarsat" },
  { title: "VDR annual performance test; DRU capsule inspection; audio test", system: "Voyage Data Recorder (VDR)", intervalDays: 365, jobType: "calendar", department: "navigation", priority: "high", classRequirement: true, componentPattern: "VDR" },
  { title: "Anti-corrosion paint inspection; cathodic protection anode inspection", system: "Structure & Hull", intervalDays: 365, jobType: "calendar", department: "deck", priority: "medium", classRequirement: true, componentPattern: "Hull|Cathodic|Anti-Corrosion" },

  // BI-ANNUAL
  { title: "Sewage tank internal structural inspection", system: "Sewage System", intervalDays: 730, jobType: "calendar", department: "engine", priority: "medium", componentPattern: "Water Holding Tank" },

  // 2-YEARLY
  { title: "Drain and inspect all fuel oil tanks", system: "Fuel Oil System", intervalDays: 730, jobType: "calendar", department: "engine", priority: "high", componentPattern: "Fuel" },
];

// ─── UPDATED SYSTEM CATEGORIES (replaces EQUIPMENT_CATEGORIES) ─────────────
export const AIRCAT35_SYSTEM_CATEGORIES = AIRCAT35_SYSTEMS.map(s => ({
  value: s.id,
  label: s.name,
  icon: s.icon,
  category: s.category,
}));

// ─── HELPER: Count components per system ───────────────────────────────────
export function countComponents(system: SystemDef): number {
  return system.subSystems.reduce((sum, ss) => sum + ss.components.length, 0);
}

export function totalComponentCount(): number {
  return AIRCAT35_SYSTEMS.reduce((sum, s) => sum + countComponents(s), 0);
}
